import React, { Fragment, useEffect, useMemo, useState } from "react";
import * as yup from "yup";
import {
  PHONE_NUMBER_FORM_VALIDATION_SCHEMA,
  EMAIL_FORM_VALIDATION_SCHEMA,
} from "../../LoanApplicationSteps/Onboarding/service";
import { useFormik, FormikProps } from "formik";
import { useLoanDashboard } from "../../../contexts";
import {
  Button,
  Modal,
  CardList,
  Dropzone,
  InputField,
  Divider,
} from "../../../components";
import {
  CardVariantType,
  DisplayVariant,
} from "../../../components/CardList/CardList";
import { ArrowDownIcon, WarningIcon } from "../../../assets/icons";
import {
  AdditionalInfo,
  AdditionalInfoType,
  LoanVendorDocumentRequestStatus,
  LoanVendorManagementAction,
  MediaPurpose,
  MediaType,
} from "../../../hooks/api/types";
import {
  useUpdateLoanAdditionalInfo,
  useMedia,
  useDeleteMedia,
  useGetApplicationVendors,
  useVendorManagementActions,
} from "../../../hooks";
import { formatBytes } from "../../../utils";
import { getFileIcon } from "../../../utils/fileIcon.util";
import PendingLoanVendorsSection from "../LoanDetailsPage/PendingLoanVendorsSection";
import { Vendor, LoanVendorStatus } from "../../../hooks/api/types";

// Mapping for document types to user-friendly messages (kept in sync with LoanCardActions)
const loanInfoRequiredActionMap: Map<AdditionalInfoType, string> = new Map([
  [
    AdditionalInfoType.SHAREHOLDERS_CONTACTS,
    "Please add email address and phone number of Shareholder Name.",
  ],
  [AdditionalInfoType.DOC_TLN, "Please upload your latest Trade License."],
  [AdditionalInfoType.DOC_POA, "Please upload your latest Power of Attorney."],
  [
    AdditionalInfoType.DOC_MOA,
    "Please upload your latest Memorandum of Association.",
  ],
  [AdditionalInfoType.DOC_VAT, "Please upload your latest VAT Statements."],
  [
    AdditionalInfoType.DOC_BANK,
    "Please upload your latest bank statement or connect your bank account for fast approval.",
  ],
  [AdditionalInfoType.DOC_SALES_STATEMENT, "Please provide sales statements."],
  [
    AdditionalInfoType.DOC_POS_SALES_STATEMENT,
    "Please provide sales statements.",
  ],
  [AdditionalInfoType.DOC_IBAN_LETTER, "Please provide iban letter."],
  [AdditionalInfoType.DOC_VAT_CERTIFICATE, "Please provide VAT certificate."],
  [AdditionalInfoType.DOC_OTHER, "Please provide additional documents."],
  [AdditionalInfoType.DOC_SALES_INVOICE, "Please provide sales Invoices."],
]);

const dontDisplayAdditionalInfo = [AdditionalInfoType.SHAREHOLDERS_AECB];

const getRequiredDocumentMessage = (info: AdditionalInfo) => {
  if (info.type === AdditionalInfoType.DOC_OTHER) return info.hint;
  if (info.type === AdditionalInfoType.SHAREHOLDERS_CONTACTS) {
    const sh: any = info?.shareholder;
    const name =
      sh?.name ||
      [sh?.firstName, sh?.lastName].filter(Boolean).join(" ") ||
      "Shareholder";
    return `Please add email address and phone number of ${name}.`;
  }
  return loanInfoRequiredActionMap.get(info.type);
};

type DocumentUploadProps = {
  info: AdditionalInfo;
  uploads: Record<string, any[]>;
  uploadingMap: Record<string, boolean>;
  handleInlineUpload: (info: AdditionalInfo, files: File[]) => Promise<void>;
  handleInlineDelete: (
    info: AdditionalInfo,
    documentId: string
  ) => Promise<void>;
};

const DocumentUpload: React.FC<DocumentUploadProps> = React.memo(
  ({ info, uploads, uploadingMap, handleInlineUpload, handleInlineDelete }) => {
    const key = info.correlationId || `${info.loanApplicationId}-${info.type}`;
    const uploaded = uploads[key] || [];
    const uploading = uploadingMap[key] || false;

    return (
      <Fragment>
        <div className="cx-w-full">
          <Dropzone
            variant={uploading ? "uploading" : "simple"}
            extensions={["Supports up to 10 files (max 5 MB each)"]}
            showExtensions={false}
            options={{
              accept: { "application/pdf": [".pdf"] },
              maxFiles: 10,
              maxSize: 5 * 1024 * 1024,
            }}
            onDrop={(files) => handleInlineUpload(info, files as File[])}
          />
        </div>
        <div className="cx-w-full cx-space-y-2 cx-mt-3">
          {uploaded.map((doc) => (
            <div className="cx-mb-2" key={doc.id}>
              <CardList
                variant={CardVariantType.UPLOADED}
                title={doc.name}
                description={formatBytes(doc.size || 0, 2)}
                fullWidth
                icon={getFileIcon(doc)}
                handleFileClose={(_, id) =>
                  handleInlineDelete(info, id || doc.id)
                }
                displayVariant={DisplayVariant.NEW}
              />
            </div>
          ))}
        </div>
      </Fragment>
    );
  }
);

type ContactsInlineProps = {
  info: AdditionalInfo;
  contactFormRefs: React.MutableRefObject<
    Record<
      string,
      FormikProps<{ countryCode: string; localNumber: string; email: string }>
    >
  >;
  updateInfo: (args: any) => Promise<any>;
  removeAdditionalInfoFromLoans: (info: AdditionalInfo) => void;
};

const ContactsInline: React.FC<ContactsInlineProps> = React.memo(
  ({ info, contactFormRefs, updateInfo, removeAdditionalInfoFromLoans }) => {
    const formik = useFormik<{
      countryCode: string;
      localNumber: string;
      email: string;
    }>({
      initialValues: {
        countryCode: "+971",
        localNumber: "",
        email: "",
      },
      validateOnMount: true,
      validationSchema: yup.object().shape({
        ...PHONE_NUMBER_FORM_VALIDATION_SCHEMA.fields,
        ...EMAIL_FORM_VALIDATION_SCHEMA.fields,
      }),
      onSubmit: async (values) => {
        const userId =
          info?.userId ??
          (info?.shareholder?.id ? Number(info.shareholder.id) : undefined);

        if (!userId) return;

        await updateInfo({
          id: info.loanApplicationId,
          infoType: info.type,
          userId,
          values: [values.countryCode, values.localNumber, values.email],
        });
        removeAdditionalInfoFromLoans(info);
      },
    });

    const key = info.correlationId || `${info.loanApplicationId}-${info.type}`;
    contactFormRefs.current[key] = formik;

    return (
      <form onSubmit={formik.handleSubmit} className="cx-w-full">
        <div className="cx-flex cx-flex-col sm:cx-flex-row cx-gap-3">
          <div className="sm:cx-w-[140px]">
            <InputField
              label="Country code"
              inputProps={{
                value: "🇦🇪 " + formik.values.countryCode,
                readOnly: true,
              }}
              error={
                formik.touched.countryCode
                  ? (formik.errors.countryCode as string)
                  : ""
              }
              hideErrorMessage
            />
          </div>
          <div className="sm:cx-flex-1">
            <InputField
              label="Phone number"
              inputProps={{
                value: formik.values.localNumber,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                  formik.setFieldValue("localNumber", e.target.value),
                onBlur: () => formik.setFieldTouched("localNumber", true),
              }}
              error={
                formik.touched.localNumber
                  ? (formik.errors.localNumber as string)
                  : ""
              }
              hideErrorMessage
            />
          </div>
          <div className="sm:cx-flex-1">
            <InputField
              label="Business email"
              inputProps={{
                value: formik.values.email,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                  formik.setFieldValue("email", e.target.value),
                onBlur: () => formik.setFieldTouched("email", true),
              }}
              error={
                formik.touched.email ? (formik.errors.email as string) : ""
              }
              hideErrorMessage
            />
          </div>
        </div>
        {(formik.touched.localNumber && formik.errors.localNumber) ||
        (formik.touched.email && formik.errors.email) ? (
          <div className="cx-text-xs cx-py-1 cx-text-text-error">
            {formik.touched.localNumber && formik.errors.localNumber
              ? (formik.errors.localNumber as string)
              : (formik.errors.email as string)}
          </div>
        ) : null}
        <div className="cx-mt-4">
          <Divider />
        </div>
      </form>
    );
  }
);

const AdditionalInfoForm: React.FC = () => {
  const {
    state: { currentLoanApplication, currentLoanApplicationId, loans },
    actions: { update, refetch },
  } = useLoanDashboard();

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [actionModalContent, setActionModalContent] = useState<any>();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const { mutateAsync: updateInfo } = useUpdateLoanAdditionalInfo();
  const { upload } = useMedia();
  const { mutateAsync: deleteMediaById } = useDeleteMedia();

  // Fetch vendors for the current loan application
  const { data: pendingVendors, refetch: refetchVendors } =
    useGetApplicationVendors({
      loanApplicationId: currentLoanApplicationId || "",
      status: LoanVendorStatus.PENDING_DOCUMENTS,
      disablePagination: true,
    });

  const filteredAdditionalInfo = useMemo(() => {
    return (
      currentLoanApplication?.additionalInfo?.filter(
        (info) => !dontDisplayAdditionalInfo.includes(info.type)
      ) || []
    );
  }, [currentLoanApplication?.additionalInfo]);

  const [uploads, setUploads] = useState<Record<string, any[]>>({});
  const [uploadingMap, setUploadingMap] = useState<Record<string, boolean>>({});
  const contactFormRefs = React.useRef<
    Record<
      string,
      FormikProps<{ countryCode: string; localNumber: string; email: string }>
    >
  >({});

  const onCancel = () => {
    update({
      currentLoanApplicationId,
      showFacilityOverview: false,
      showAdditionalInfoRequired: false,
    });
  };

  const { mutateAsync: finalizeVendorManagement, isLoading: finalizing } =
    useVendorManagementActions();

  const getPendingActions = () => {
    return filteredAdditionalInfo.filter((info) => {
      const key =
        info.correlationId || `${info.loanApplicationId}-${info.type}`;
      if (info.type === AdditionalInfoType.SHAREHOLDERS_CONTACTS) {
        const formik = contactFormRefs.current[key];
        return !formik || !formik.dirty || !formik.isValid;
      } else {
        const uploaded = uploads[key] || [];
        return uploaded.length === 0;
      }
    });
  };

  const submitAllActions = async () => {
    if (pendingVendors?.data?.length && pendingVendors?.data?.length > 0) {
      try {
        await finalizeVendorManagement({
          loanApplicationId: currentLoanApplicationId,
          type: LoanVendorManagementAction.SUBMIT_DOCUMENTS,
        });
        await refetchVendors();
        await refetch();
      } catch (error) {}
    }

    const submissionPromises = filteredAdditionalInfo.map((info) => {
      const key =
        info.correlationId || `${info.loanApplicationId}-${info.type}`;

      if (info.type === AdditionalInfoType.SHAREHOLDERS_CONTACTS) {
        const formik = contactFormRefs.current[key];
        if (formik && formik.isValid && formik.dirty) {
          return formik.submitForm();
        }
      } else {
        const uploaded = uploads[key] || [];
        if (uploaded.length > 0) {
          const ids = uploaded.map((d) => d.id);
          return updateInfo({
            id: info.loanApplicationId,
            infoType: info.type,
            values: ids,
          }).then(() => {
            removeAdditionalInfoFromLoans(info);
          });
        }
      }
      return Promise.resolve(); // Resolve for items that don't need action
    });

    await Promise.all(submissionPromises);
    onCancel();
  };

  // Remove a resolved additional-info item from local state (updates loans cache)
  const removeAdditionalInfoFromLoans = (info: AdditionalInfo) => {
    const byCorrelation = (x: AdditionalInfo) =>
      info.correlationId
        ? x.correlationId !== info.correlationId
        : x.type !== info.type;

    const updatedLoans = loans.map((loan) =>
      loan.id === info.loanApplicationId
        ? {
            ...loan,
            additionalInfo: (loan.additionalInfo || []).filter(byCorrelation),
          }
        : loan
    );

    const updatedCurrent =
      currentLoanApplication?.id === info.loanApplicationId
        ? {
            ...currentLoanApplication,
            additionalInfo: (
              currentLoanApplication.additionalInfo || []
            ).filter(byCorrelation),
          }
        : currentLoanApplication;

    update({ loans: updatedLoans, currentLoanApplication: updatedCurrent });
  };

  const handleInlineUpload = async (info: AdditionalInfo, files: File[]) => {
    if (!files?.length) return;
    const key = info.correlationId || `${info.loanApplicationId}-${info.type}`;
    setUploadingMap((m) => ({ ...m, [key]: true }));
    try {
      const ids = await Promise.all(
        files.map((file) =>
          upload({
            file,
            type: MediaType.DOCUMENT,
            purpose: mapDocPurpose(info.type),
            loanApplicationId: info.loanApplicationId,
          })
        )
      );

      const newFiles = files.map((f, i) => ({
        name: f.name,
        size: f.size,
        id: ids[i],
      }));
      setUploads((u) => ({ ...u, [key]: [...(u[key] || []), ...newFiles] }));
    } finally {
      setUploadingMap((m) => ({ ...m, [key]: false }));
    }
  };

  const handleInlineDelete = async (
    info: AdditionalInfo,
    documentId: string
  ) => {
    const key = info.correlationId || `${info.loanApplicationId}-${info.type}`;
    await deleteMediaById(documentId);
    setUploads((u) => ({
      ...u,
      [key]: (u[key] || []).filter((d) => d.id !== documentId),
    }));
  };

  const vendorHasPendingActions = useMemo(() => {
    if (!pendingVendors?.data) return false;

    return pendingVendors.data.some((vendor: Vendor) => {
      if (
        !vendor.loanVendorDocumentRequests ||
        vendor.loanVendorDocumentRequests.length === 0
      ) {
        return true;
      }

      return vendor.loanVendorDocumentRequests.some((request) => {
        return (
          !request.loanVendorDocuments ||
          request.loanVendorDocuments.length === 0
        );
      });
    });
  }, [pendingVendors]);

  function mapDocPurpose(type: AdditionalInfoType): MediaPurpose {
    switch (type) {
      case AdditionalInfoType.DOC_TLN:
        return MediaPurpose.TRADE_LICENSE;
      case AdditionalInfoType.DOC_MOA:
        return MediaPurpose.MEMORANDUM_OF_ASSOCIATION;
      case AdditionalInfoType.DOC_POA:
        return MediaPurpose.POWER_OF_ATTORNEY;
      case AdditionalInfoType.DOC_VAT:
        return MediaPurpose.VAT_STATEMENT;
      case AdditionalInfoType.DOC_VAT_CERTIFICATE:
        return MediaPurpose.VAT_CERTIFICATE;
      case AdditionalInfoType.DOC_BANK:
        return MediaPurpose.BANK_STATEMENT;
      case AdditionalInfoType.DOC_SALES_STATEMENT:
        return MediaPurpose.SALES_STATEMENT;
      case AdditionalInfoType.DOC_POS_SALES_STATEMENT:
        return MediaPurpose.POS_SALES_STATEMENT;
      case AdditionalInfoType.DOC_SALES_INVOICE:
        return MediaPurpose.INVOICE;
      case AdditionalInfoType.DOC_IBAN_LETTER:
        return MediaPurpose.IBAN_LETTER;
      case AdditionalInfoType.DOC_OTHER:
      default:
        return MediaPurpose.GENERIC_DOCUMENT;
    }
  }

  return (
    <div className="cx-w-full">
      <div className="cx-max-w-[960px] cx-mx-auto cx-px-4 sm:cx-px-6">
        {/* Additional Info Section */}
        <div className="cx-bg-white cx-rounded-lg cx-shadow-sm cx-border cx-border-neutral-light cx-overflow-hidden">
          {(filteredAdditionalInfo.length > 0 ||
            (pendingVendors?.data?.length &&
              pendingVendors?.data?.length > 0)) && (
            <div
              className="cx-px-4 sm:cx-px-6 cx-py-3 cx-bg-gray-50 cx-border-b cx-cursor-pointer hover:cx-bg-gray-100 cx-transition-colors"
              onClick={() => setIsCollapsed(!isCollapsed)}
            >
              <div className="cx-flex cx-items-start sm:cx-items-center cx-gap-2 cx-justify-between">
                <div className="cx-flex cx-items-start sm:cx-items-center cx-gap-2 cx-min-w-0 cx-flex-wrap">
                  <span className="cx-shrink-0 cx-flex cx-items-center cx-h-5">
                    <WarningIcon
                      className="cx-text-[#DA1E28]"
                      width={18}
                      height={18}
                    />
                  </span>
                  <span className="cx-text-[#A2191F] cx-font-medium">
                    {" "}
                    {pendingVendors?.data?.length &&
                    pendingVendors?.data?.length > 0
                      ? filteredAdditionalInfo.length + 1
                      : filteredAdditionalInfo.length}{" "}
                    Actions Required:
                  </span>{" "}
                  Please complete to proceed
                </div>
                <ArrowDownIcon className="cx-text-text-secondary cx-w-3 cx-h-3 cx-shrink-0 cx-mt-1.5 sm:cx-mt-0" />
              </div>
            </div>
          )}

          {/* List */}
          {!isCollapsed && (
            <>
              <div className="cx-p-4 sm:cx-p-6">
                {filteredAdditionalInfo.length > 0 ||
                (pendingVendors?.data?.length &&
                  pendingVendors?.data?.length > 0) ? (
                  <div className="cx-space-y-3">
                    {filteredAdditionalInfo.map((info: AdditionalInfo) => (
                      <div
                        key={
                          info.correlationId ||
                          `${info.loanApplicationId}-${info.type}`
                        }
                        className="cx-flex cx-flex-col cx-gap-3 cx-p-4 cx-bg-white cx-border cx-border-neutral-light cx-rounded-lg"
                      >
                        <div className="cx-flex cx-items-start cx-gap-3">
                          <WarningIcon
                            className="cx-text-yellow-600 cx-flex-shrink-0 cx-mt-0.5"
                            width={16}
                            height={16}
                          />
                          <div className="cx-text-sm cx-text-text-primary">
                            {getRequiredDocumentMessage(info)}
                          </div>
                        </div>
                        <Divider />
                        <div className="cx-w-full cx-flex cx-flex-col">
                          {info.type ===
                          AdditionalInfoType.SHAREHOLDERS_CONTACTS ? (
                            <ContactsInline
                              info={info}
                              contactFormRefs={contactFormRefs}
                              updateInfo={updateInfo}
                              removeAdditionalInfoFromLoans={
                                removeAdditionalInfoFromLoans
                              }
                            />
                          ) : (
                            <DocumentUpload
                              info={info}
                              uploads={uploads}
                              uploadingMap={uploadingMap}
                              handleInlineUpload={handleInlineUpload}
                              handleInlineDelete={handleInlineDelete}
                            />
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Pending Vendors Section */}
                    {Boolean(
                      pendingVendors?.data?.length &&
                        pendingVendors?.data?.length > 0
                    ) && (
                      <div
                        key={
                          pendingVendors?.data
                            ? "pending-vendors"
                            : "no-pending-vendors"
                        }
                        className="cx-flex cx-flex-col cx-gap-3 cx-p-4 cx-bg-white cx-border cx-border-neutral-light cx-rounded-lg"
                      >
                        <div className="cx-flex cx-items-start cx-gap-3">
                          <WarningIcon
                            className="cx-text-yellow-600 cx-flex-shrink-0 cx-mt-0.5"
                            width={16}
                            height={16}
                          />
                          <div className="cx-text-sm cx-text-text-primary">
                            Some of your vednors are pending. Open the vendors
                            and take necessary action
                          </div>
                        </div>
                        <Divider />
                        <div className="cx-w-full cx-flex cx-flex-col">
                          <PendingLoanVendorsSection
                            loanApplication={currentLoanApplication!}
                            vendors={pendingVendors?.data}
                            onSuccess={() => {
                              refetchVendors();
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="cx-min-h-[100px] cx-flex cx-items-center cx-justify-center cx-text-text-secondary">
                    No additional information required for the loan application
                  </div>
                )}
              </div>

              {(filteredAdditionalInfo.length > 0 ||
                (pendingVendors?.data?.length &&
                  pendingVendors?.data?.length > 0)) && (
                <div className="cx-px-4 sm:cx-px-6 cx-py-4 cx-border-t cx-border-neutral-light">
                  <div className="cx-flex cx-flex-col-reverse sm:cx-flex-row sm:cx-justify-end cx-gap-3">
                    <Button
                      label="Cancel"
                      outlined
                      onClick={onCancel}
                      className="cx-w-full sm:cx-w-auto"
                    />
                    <Button
                      label="Submit"
                      onClick={async () => {
                        const pendingActions = getPendingActions();
                        if (pendingActions.length > 0) {
                          setShowConfirmationModal(true);
                        } else {
                          await submitAllActions();
                        }
                      }}
                      disabled={vendorHasPendingActions}
                      className="cx-w-full sm:cx-w-auto"
                    />
                  </div>
                </div>
              )}
            </>
          )}

          <Modal
            isOpen={showConfirmationModal}
            onClose={() => setShowConfirmationModal(false)}
          >
            <div className="cx-p-3 cx-text-center">
              <h2 className="cx-text-2xl cx-font-semibold cx-mb-2">
                Some actions are still pending
              </h2>
              <p className="cx-text-text-secondary cx-mb-6">
                You've completed{" "}
                {filteredAdditionalInfo.length - getPendingActions().length} of{" "}
                {filteredAdditionalInfo.length} required actions. Would you like
                to continue anyway?
              </p>
              <div className="cx-flex cx-flex-col sm:cx-flex-row cx-gap-3">
                <Button
                  label="Proceed anyway"
                  outlined
                  onClick={async () => {
                    setShowConfirmationModal(false);
                    await submitAllActions();
                  }}
                  className="cx-w-full sm:cx-flex-1 cx-whitespace-nowrap"
                />
                <Button
                  label="Perform actions"
                  onClick={() => setShowConfirmationModal(false)}
                  className="cx-w-full sm:cx-flex-1 cx-whitespace-nowrap"
                />
              </div>
            </div>
          </Modal>

          {/* Modal */}
          <Modal
            isOpen={actionModalOpen}
            onClose={() => setActionModalOpen(false)}
          >
            {actionModalContent}
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default AdditionalInfoForm;
